import React, { useEffect, useMemo, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";

import { useNavigate, useParams } from "react-router-dom";

import Button from "components/form/Button";
import { ActionNoData } from "icons/dashboard/actionNoData";
import { AddPaymentMethodIcon } from "icons/dashboard/addPaymentMethod";
import { EditCard } from "icons/dashboard/editCard";
import { BankIconNew } from "icons/payments/BankIconNew";
import { MastercardIcon } from "icons/payments/MastercardIcon";
import { useDashboardData } from "store/slices/dashboardSlice";
import {
  encode,
  formatCurrency,
  getBankLogo,
  isCardExpiredSoon,
  isCreditCardExpired,
  maskString,
} from "utils/HelperFunctions";
import PaymentAgreementIcon from "icons/header/PaymentAgreementIcon";
import PaymentLinkIcon from "icons/header/PaymentLinkIcon";
import {
  useBankData,
  useCardData,
  useOldWalletData,
  useIsDataLoading,
} from "store/slices/paymentMethodSlice";
import { GooglePayIcon } from "icons/payments/GooglePayIcon";
import { VisaIcon } from "icons/payments/VisaIcon";
import JCB from "../../icons/payments/JCB.svg";
import amex from "../../icons/payments/amex.svg";
import dinersClub from "../../icons/payments/dinersClub.svg";
import Discover from "icons/payments/Discover";
import { DollarSquareIcon } from "icons/payments/DollarSquareIcon";
import PaymentMethodWalletCard from "components/paymentMethod/PaymentMethodWalletCard";
import Drawer from "components/ui-component/SideDrawer";
import AddEditCardForm from "components/paymentMethod/AddEditCardForm";
import AddEditBankForm from "components/paymentMethod/AddEditBankForm";
import UnionPay from "icons/payments/UnionPay";
import { decode } from "utils/HelperFunctions";
import InvoiceIcon from "icons/header/InvoiceIcon";
import QuoteIcon from "icons/header/QuoteIcon";
import { useMediaQuery } from "react-responsive";
import moment from "moment";
import { useIsTableLoading } from "store/slices/actionSlice";
import { PaymentGateway } from "types/payments";
import { useTid } from "utils/testid";
import { formatCardExpiry } from "utils/DateFormat";
import { getActionNavigationSlug } from "utils/actionRoute";

interface PaymentMethodSectionProps {
  actionList: any;
  isLoading: boolean;
  onGetDashboardData: any;
  onOldCardDataGet: any;
}

// Default methods that render as a simple branded block. Card, bank, Google Pay
// and Cash App have their own richer layouts above. Wire arrives as either code.
const WALLET_METHOD_LABELS: { [key: string]: string } = {
  PP: "PayPal",
  AMZP: "Amazon Pay",
  AP: "Apple Pay",
  WT: "Wire Transfer",
  WTO: "Wire Transfer",
};

const PaymentMethodSection = ({
  actionList,
  isLoading,
  onGetDashboardData,
  onOldCardDataGet,
}: PaymentMethodSectionProps) => {
  const tid = useTid();
  const isLessThan590 = useMediaQuery({ query: "(max-width: 590px)" });

  const [hoverCard, setHoverCard] = useState<boolean>(false);
  const [mouseHoverId, setMouseHoverId] = useState<number | null>(null);
  const [isAddCard, setIsAddCard] = useState<boolean>(false);
  const [isEditCard, setIsEditCard] = useState<boolean>(false);
  const [editCardData, setEditCardData] = useState<any>(null);
  const [isAddBank, setIsAddBank] = useState<boolean>(false);
  const [isEditBank, setIsEditBank] = useState<boolean>(false);
  const [editBankData, setEditBankData] = useState<any>(null);
  const oldCardData = useCardData();
  const oldBankData = useBankData();
  const oldWalletData = useOldWalletData();

  const dashboardData = useDashboardData();

  // Which saved method is "the default" is decided by default_profile — that is
  // the flag the Payment Methods screen sets and badges, so it is the one the
  // customer actually chose. A separate `default` field also exists and can be
  // set on several methods at once; reading it here made this panel disagree
  // with the Payment Methods screen. defaultCardDetail from the server is the
  // last resort: it arrives empty even when a default is clearly set, and does
  // not appear to cover wallets.
  const defaultMethod = useMemo(() => {
    const saved = [
      ...(oldCardData || []),
      ...(oldBankData || []),
      ...(oldWalletData || []),
    ];
    const chosen =
      saved.find((m: any) => m?.default_profile === "Y") ||
      saved.find((m: any) => m?.default === "Y");
    if (chosen) return chosen;
    const fromServer = dashboardData?.defaultCardDetail;
    return fromServer && Object.keys(fromServer).length > 0 ? fromServer : null;
  }, [oldCardData, oldBankData, oldWalletData, dashboardData]);
  const [status, setStatus] = useState("");
  const navigate = useNavigate();
  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);

  const isDataLoading = useIsDataLoading();
  // The action list is fetched separately from the rest of the dashboard and
  // lands later, so it needs its own loading flag — otherwise the section drops
  // to its "nothing here" state for a few seconds before the items arrive.
  const isActionListLoading = useIsTableLoading();

  // Show as many actions as the space below the card actually allows, rather
  // than a fixed five that leaves a gap on taller screens.
  const ACTION_ROW_HEIGHT = 44;
  const ACTION_ROW_GAP = 24;
  const FOOTER_ALLOWANCE = 78; // fixed footer bar plus breathing room
  const ACTION_FETCH_LIMIT = 12; // must match the limit the dashboard requests
  const actionListRef = useRef<HTMLDivElement>(null);
  const [visibleActionCount, setVisibleActionCount] = useState<number>(5);

  useEffect(() => {
    const recalculate = () => {
      const el = actionListRef.current;
      // This section is mounted twice — once in the right rail, once inline for
      // narrower screens — and only one is displayed at a time. The hidden copy
      // measures as zero, which would otherwise yield a nonsense row count.
      if (!el || !el.offsetParent) return;
      // Only the wide layout puts this in a fixed-height right rail where
      // filling the leftover space makes sense. Below that it sits inline in a
      // long scrolling page, where measuring against the window would keep
      // adding rows; the design calls for a fixed list there.
      if (window.innerWidth < 1536) {
        setVisibleActionCount((prev) => (prev === 5 ? prev : 5));
        return;
      }
      const available =
        window.innerHeight -
        el.getBoundingClientRect().top -
        FOOTER_ALLOWANCE;
      // Rows are not a fixed height — a long title wraps onto a second line — so
      // size against the tallest one actually rendered rather than a guess.
      const rendered = Array.from(el.children) as HTMLElement[];
      const rowHeight = rendered.length
        ? Math.max(
            ACTION_ROW_HEIGHT,
            ...rendered.map((row) => row.getBoundingClientRect().height)
          )
        : ACTION_ROW_HEIGHT;
      const fits = Math.floor(
        (available + ACTION_ROW_GAP) / (rowHeight + ACTION_ROW_GAP)
      );
      const next = Math.min(ACTION_FETCH_LIMIT, Math.max(3, fits));
      setVisibleActionCount((prev) => (prev === next ? prev : next));
    };

    recalculate();
    window.addEventListener("resize", recalculate);
    return () => window.removeEventListener("resize", recalculate);
    // visibleActionCount is included so the measurement settles: the first pass
    // sizes against whatever is on screen, and any correction re-measures once
    // more before coming to rest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionList, isLoading, isActionListLoading, visibleActionCount]);

  useEffect(() => {
    if (defaultMethod?.cc_expiry) {
      const isExpired = isCreditCardExpired(
        defaultMethod?.cc_expiry
      );
      const isExpiringSoon = isCardExpiredSoon(
        defaultMethod?.cc_expiry
      );
      const getCardStatus = () => {
        if (isExpired) return "Expired";
        if (isExpiringSoon) return "Expires Soon";
        return "";
      };
      setStatus(getCardStatus());
    }
  }, [dashboardData]);

  const imgUrl = (cardType: string | undefined): React.ReactNode => {
    switch (cardType?.toLowerCase()) {
      case "visa":
        return <VisaIcon />;
      case "amex":
      case "american express":
      case "americanexpress":
      case "american expres":
        return <img src={amex} alt="AMEX" className="object-contain" />;
      case "mc":
      case "master":
      case "mastercard":
        return <MastercardIcon />;
      case "discover":
      case "ds":
        return <Discover />;
      case "jcb":
        return <img src={JCB} alt="JCB" className="object-contain" />;
      case "diners":
      case "dinersclub":
      case "diners club":
      case "dc":
        return <img src={dinersClub} alt="DC" />;
      case "unionpay":
        return <UnionPay />;
      default:
        return "";
    }
  };
  return (
    <>
      <div
        className="w-full"
        data-testid={tid("payment-method-section")}
        aria-label="Default payment method"
      >
        {(isDataLoading || isLoading) && (
          <div className="w-full max-w-[420px] rounded-[16px]">
            <Skeleton height={252} />
          </div>
        )}
        {!isDataLoading &&
          !isLoading &&
          // Wallets count too. Gating on cards and banks alone meant a customer
          // whose only saved method is PayPal (or Amazon/Apple Pay) was shown
          // the "no payment methods at all" state.
          (oldCardData.length !== 0 ||
          oldBankData.length !== 0 ||
          (oldWalletData?.length ?? 0) !== 0 ? (
            <div className="w-full xl:block grid place-items-center">
              {defaultMethod?.payment_method === "CC" ? (
                <div
                  className="relative portalBackgroundCard w-full rounded-[16px]  cursor-pointer max-w-[420px]"
                  data-testid={tid("default-card-payment-method")}
                  aria-label="Default card payment method"
                  onMouseEnter={() => setHoverCard(true)}
                  onMouseLeave={() => setHoverCard(false)}
                  onClick={() => setHoverCard(!hoverCard)}
                >
                  <div className="flex rounded-tl-[16px]">
                    <div className="bg-txtPrimary w-[30px] rounded-tl-[16px]"></div>
                    <div
                      className={`bg-txtPrimary py-[6px] pr-[15px] text-white text-sm font-medium ${
                        hoverCard ? "opacity-30" : ""
                      }`}
                      style={{
                        clipPath: "polygon(0 0, 100% 0%, 86% 100%, 0% 100%)",
                      }}
                    >
                      Default
                    </div>
                    {status && (
                      <div
                        className={`${hoverCard ? "opacity-30" : ""} ${
                          // Already expired is the more serious state and the
                          // design gives it red; the earlier warning stays yellow.
                          status === "Expired" ? "bg-red" : "bg-yellow"
                        } py-[6px] px-[16px] text-white text-sm ml-[-11px] font-medium`}
                        style={{
                          clipPath: "polygon(10% 0, 100% 0%, 90% 99%, 0% 100%)",
                        }}
                      >
                        {status}
                      </div>
                    )}
                  </div>
                  <div
                    className={`${
                      hoverCard ? "opacity-30" : ""
                    } flex justify-end px-[30px] mt-2 `}
                  >
                    {imgUrl(defaultMethod?.cc_type)}
                  </div>

                  <p
                    className={`text-xl mt-[30px] px-[30px] ${
                      hoverCard ? "!text-[#5E6482]" : "text-white"
                    } font-medium`}
                  >{`XXXX XXXX XXXX ${
                    defaultMethod?.cc_4digit || "----"
                  }`}</p>

                  <div className="px-[30px] pb-[30px] flex justify-between text-white mt-[50px] max-3xl:mt-7">
                    <div>
                      <p
                        className={`text-base font-normal ${
                          hoverCard ? "!text-[#5E6482]" : ""
                        }`}
                      >
                        NAME
                      </p>
                      <p
                        className={`text-xl font-medium ${
                          hoverCard ? "!text-[#5E6482]" : ""
                        }`}
                      >
                        {defaultMethod?.account_name ||
                          "---"}
                      </p>
                    </div>
                    <div>
                      <p
                        className={`${
                          hoverCard ? "!text-[#5E6482]" : ""
                        } text-base font-normal`}
                      >
                        VALID THRU
                      </p>
                      <p
                        className={`text-xl font-medium ${
                          hoverCard ? "!text-[#5E6482]" : ""
                        }`}
                      >
                        {defaultMethod?.cc_expiry
                          ? formatCardExpiry(defaultMethod.cc_expiry)
                          : "-- --"}
                      </p>
                    </div>
                  </div>

                  {hoverCard &&
                    (defaultMethod?.payment_method ===
                      "CC" ||
                      defaultMethod?.payment_method ===
                        "ACH") && (
                      <>
                        <div
                          className="absolute bottom-3 right-3 cursor-pointer"
                          role="button"
                          tabIndex={0}
                          data-testid={tid(
                            "edit-default-payment-method-button"
                          )}
                          aria-label="Edit default payment method"
                          onClick={() => {
                            if (
                              defaultMethod
                                ?.payment_method === "ACH"
                            ) {
                              setEditBankData({
                                ...defaultMethod,
                              });
                              setIsEditBank(true);
                              setIsAddBank(true);
                            } else {
                              let slug = encode(
                                `${
                                  defaultMethod?.id
                                }_${clusterId}_${new Date().getTime()}`,
                                "pm_"
                              );
                              setEditCardData(slug);
                              setIsEditCard(true);
                              setIsAddCard(true);
                            }
                          }}
                        >
                          <EditCard />
                        </div>
                      </>
                    )}
                </div>
              ) : defaultMethod?.payment_method === "ACH" ? (
                <div
                  className="w-full relative text-center flex flex-col items-center rounded-[12px] bg-bgSecondary py-12 px-[56px] max-w-[420px]"
                  data-testid={tid("default-bank-payment-method")}
                  aria-label="Default bank payment method"
                >
                  <div className="flex rounded-tl-[16px] absolute top-0 left-0">
                    <div className="bg-txtPrimary w-[30px] rounded-tl-[16px]"></div>
                    <div
                      className={`bg-txtPrimary py-[6px] pr-[15px] pl-0.5 text-white text-sm font-medium ${
                        hoverCard ? "opacity-30" : ""
                      }`}
                      style={{
                        clipPath: "polygon(0 0, 100% 0%, 86% 100%, 0% 100%)",
                      }}
                    >
                      Default
                    </div>

                    {defaultMethod?.is_connected === "N" &&
                    (defaultMethod?.gateway_name ===
                      PaymentGateway?.STRIPE ||
                      (defaultMethod?.pc_id &&
                        defaultMethod?.pc_id > 0)) ? (
                      <div
                        className={`${
                          hoverCard ? "opacity-30" : ""
                        } bg-yellow py-[6px] px-[16px] text-white text-sm ml-[-11px] font-medium`}
                        style={{
                          clipPath: "polygon(10% 0, 100% 0%, 90% 99%, 0% 100%)",
                        }}
                      >
                        {status}
                      </div>
                    ) : (
                      ""
                    )}
                  </div>
                  <div className="text-txtSecondary text-xl font-medium mt-4">
                    {defaultMethod?.bank_name || "Test Bank"}
                  </div>
                  <div>
                    {getBankLogo({
                      bankName: defaultMethod?.bank_name,
                      type: "big",
                    }) || <BankIconNew />}
                  </div>
                  <div className="mt-[24px] text-lg text-txtParagraph">
                    {maskString(
                      defaultMethod?.account_number
                    ) || "****1234"}
                  </div>
                </div>
              ) : defaultMethod?.payment_method === "GP" ? (
                <div
                  className="w-full text-center flex flex-col items-center rounded-[12px] bg-bgSecondary py-12 px-[56px] max-w-[420px]"
                  data-testid={tid("default-google-pay-payment-method")}
                  aria-label="Default Google Pay payment method"
                >
                  <div>
                    <GooglePayIcon />
                  </div>
                  <div className="text-txtSecondary text-xl font-medium mt-4">
                    GooglePay
                  </div>
                  <div className="mt-[24px] text-lg flex gap-1 text-txtParagraph justify-center items-center">
                    {defaultMethod?.cc_type?.toLowerCase() ===
                    "visa" ? (
                      <VisaIcon />
                    ) : defaultMethod?.cc_type?.toLowerCase() ===
                      "mc" ? (
                      <MastercardIcon />
                    ) : defaultMethod?.cc_type?.toLowerCase() ===
                      "amex" ? (
                      <img src={amex} alt="AMEX" className="object-contain" />
                    ) : defaultMethod?.cc_type?.toLowerCase() ===
                      "discover" ? (
                      <Discover />
                    ) : (
                      <img src={JCB} alt="JCB" />
                    )}
                    {maskString(defaultMethod?.cc_4digit) ||
                      "****1234"}
                  </div>
                </div>
              ) : defaultMethod?.payment_method ===
                "CAPP" ? (
                <div
                  className="w-full text-center flex flex-col items-center rounded-[12px] bg-bgSecondary py-14 px-[56px] max-w-[420px]"
                  data-testid={tid("default-cash-app-payment-method")}
                  aria-label="Default Cash App payment method"
                >
                  <div>
                    <DollarSquareIcon />
                  </div>
                  <div className="text-txtSecondary text-xl font-medium mt-4">
                    Cash App
                  </div>
                  <div className="mt-[24px] text-lg flex gap-1 text-txtParagraph justify-center items-center"></div>
                </div>
              ) : WALLET_METHOD_LABELS[
                  defaultMethod?.payment_method
                ] ? (
                // Reuses the exact card the Payment Methods screen renders, so a
                // wallet looks identical in both places. readOnly drops the
                // delete / set-default controls, which belong on that screen.
                <div
                  className="w-full flex justify-center"
                  data-testid={tid("default-wallet-payment-method")}
                >
                  <PaymentMethodWalletCard
                    val={defaultMethod}
                    idx={0}
                    onDelete={() => {}}
                    onDefault={() => {}}
                    readOnly
                    // 374x250 is the dashboard's card slot in the design — the
                    // same box the credit-card block and the empty-state
                    // placeholder occupy.
                    sizeClassName="h-[250px] w-full lg:w-[374px] max-w-full"
                  />
                </div>
              ) : (
                // 250px matches the card this stands in for, per the design's
                // "No payment method" frame — at 275px the whole right-hand
                // column changed height depending on whether anything was saved.
                <div
                  className="w-full text-center flex flex-col items-center justify-center rounded-[12px] bg-bgSecondary h-[250px] max-w-[420px]"
                  data-testid={tid("no-default-payment-method")}
                >
                  <div>
                    <AddPaymentMethodIcon />
                  </div>
                  <div className="text-txtSecondary text-base font-normal mt-4">
                    Add a payment method for quick and easy checkouts.
                  </div>
                  <div className="mt-[24px]">
                    <Button
                      text="Add payment method"
                      onClick={() => navigate(`/${cluster_id}/payment-methods`)}
                      data-testid={tid("add-payment-method-button")}
                      aria-label="Add payment method"
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div
              className="w-full text-center flex flex-col items-center rounded-[12px] bg-bgSecondary py-[27px] px-[56px]"
              data-testid={tid("empty-payment-methods")}
            >
              <div>
                <AddPaymentMethodIcon />
              </div>
              <div className="text-txtSecondary text-base font-normal mt-4">
                Add a payment method for quick and easy checkouts.
              </div>
              <div className="mt-[24px]">
                <Button
                  text="Add payment method"
                  onClick={() => navigate(`/${cluster_id}/payment-methods`)}
                  data-testid={tid("add-payment-method-button")}
                  aria-label="Add payment method"
                />
              </div>
            </div>
          ))}
      </div>

      <div
        className="mt-10 h-fit w-full pb-5"
        data-testid={tid("dashboard-action-center")}
        aria-label="Action center"
      >
        <div className="flex justify-between">
          <p className="2xl:text-xl text-lg font-medium text-txtPrimary">
            Action Center
          </p>
          {actionList && actionList.length > 0 && (
            <p
              className="text-blue text-sm underline font-medium cursor-pointer hover:!text-btnPrimaryHover"
              role="link"
              tabIndex={0}
              data-testid={tid("action-center-view-all-link")}
              aria-label="View all action center items"
              onClick={() => navigate(`/${cluster_id}/action-center`)}
            >
              View all
            </p>
          )}
        </div>

        {isLoading || isActionListLoading ? (
          // Mirrors the real rows: same count, same 24px rhythm, same icon and
          // text geometry. Sizes go through the skeleton's own width/height
          // props — utility classes do not drive its dimensions, which left the
          // text bars with no width and the "circle" as an ellipse.
          // Same ref as the loaded list, so the row count is measured while the
          // placeholder is on screen and the list does not jump when data lands.
          <div
            ref={actionListRef}
            className="flex flex-col gap-6 h-fit pt-4 pb-4 w-full"
          >
            {Array.from({ length: visibleActionCount }).map((item, index) => (
              <div
                className="w-full flex gap-[15px] items-center"
                key={index}
                aria-hidden="true"
              >
                <span className="shrink-0 leading-none">
                  <Skeleton circle width={40} height={40} />
                </span>
                <div className="flex-1 min-w-0 flex flex-col gap-1">
                  <Skeleton height={18} width="85%" />
                  <Skeleton height={13} width="55%" />
                </div>
              </div>
            ))}
          </div>
        ) : actionList && actionList.length > 0 ? (
          <div
            ref={actionListRef}
            className="flex flex-col gap-6 overflow-y-clip h-fit pt-4 pb-4 relative"
          >
            {actionList
              .slice(0, visibleActionCount)
              .map((item: any, index: number, shownList: any[]) => (
              <div
                className="w-full flex gap-[15px] hover:bg-bgThird cursor-pointer items-center rounded-md py-[2px] relative bg-bgPrimary"
                data-testid={tid(`action-center-row-${item?.id ?? index}`)}
                aria-label={`Action center item ${index + 1}`}
                onMouseEnter={() => setMouseHoverId(index)}
                onMouseLeave={() => setMouseHoverId(null)}
                key={index}
              >
                {index !== shownList.length - 1 && (
                  <div className="absolute w-[1px] h-full left-5 top-10 bg-strokePrimary -z-[1]" />
                )}
                <div className="w-[40px] h-[40px] p-[10px] grid place-items-center bg-bgSecondary rounded-full">
                  {item.action === "payment_link" ? (
                    <PaymentLinkIcon />
                  ) : item.action === "agreement" ? (
                    <PaymentAgreementIcon />
                  ) : item.action === "invoice" ? (
                    <InvoiceIcon />
                  ) : (
                    <QuoteIcon />
                  )}
                </div>
                <div className="inline-flex items-center justify-between gap-5 w-full">
                  {/* flex-1 rather than a fixed 90%: the text should use all the
                      room left beside the button, which keeps most titles on one
                      line and so keeps the rows evenly spaced. */}
                  <div className="flex flex-col lg:gap-0 gap-1 flex-1 min-w-0">
                    <p
                      className={`break-words whitespace-normal text-[15px] font-medium text-txtPrimary`}
                    >
                      {/* A payment link may legitimately be sent without an amount,
                          so drop the "for <amount>" clause entirely rather than
                          printing a placeholder mid-sentence. Every other action
                          type always carries an amount. */}
                      {item.action === "payment_link" ? (
                        Number(item?.total_amount) > 0 ? (
                          `You have a payment link for ${formatCurrency(
                            item?.total_amount || 0
                          )}`
                        ) : (
                          "You have a payment link"
                        )
                      ) : (
                        <>
                          {item.action === "agreement"
                            ? "You have an agreement for"
                            : item.action === "invoice"
                              ? "You have an invoice for"
                              : "You have a quote for"}{" "}
                          {item?.total_amount
                            ? formatCurrency(item?.total_amount || 0)
                            : "$0.00"}
                        </>
                      )}
                    </p>
                    <p
                      className={` text-xs font-medium text-txtSecondary 2xl:py-0 py-1`}
                    >
                      {item?.action === "payment_link" && (
                        <>
                          {item?.sourceStatus === "Expired"
                            ? "expired on"
                            : item?.sourceStatus === "Completed"
                              ? " completed on"
                              : item?.sourceStatus === "Opened"
                                ? "due by"
                                : "due by"}
                        </>
                      )}
                      {item?.action === "invoice" && (
                        <>
                          {item?.sourceStatus?.toLowerCase() === "paid"
                            ? "completed on"
                            : item?.sourceStatud?.toLowerCase() === "cancelled"
                              ? "canceled on"
                              : item?.sourceStatus?.toLowerCase() === "opened"
                                ? "due by"
                                : "due by"}
                        </>
                      )}
                      {item?.action === "quotes" && (
                        <>
                          {item?.sourceStatus?.toLowerCase() === "accepted"
                            ? "completed on"
                            : item?.sourceStatud?.toLowerCase() === "canceled"
                              ? "canceled on"
                              : item?.sourceStatus?.toLowerCase() === "expired"
                                ? "expired on"
                                : item?.sourceStatus?.toLowerCase() === "open"
                                  ? "due by"
                                  : "due by"}
                        </>
                      )}
                      {item?.action === "agreement" && (
                        <>
                          {item?.sourceStatus === "Expired"
                            ? "expired on"
                            : item?.sourceStatus === "Completed"
                              ? "completed on"
                              : item?.sourceStatus === "Accepted"
                                ? "completed on"
                                : item?.sourceStatus === "Cancelled"
                                  ? "canceled on"
                                  : item?.sourceStatus === "past_due"
                                    ? "expires on"
                                    : item?.sourceStatus === "Opened"
                                      ? "due by"
                                      : "due by"}
                        </>
                      )}{" "}
                      {item.action === "payment_link" && (
                        <>
                          {item?.sourceStatus === "Expired" ||
                          item?.sourceStatus === "past_due"
                            ? moment(item?.expiry_date).format("MMMM D, YYYY")
                            : item?.sourceStatus === "Completed"
                              ? moment(item.CompletedOn).format("MMMM D, YYYY")
                              : item?.sourceStatus === "Opened"
                                ? moment(item.due).format("MMMM D, YYYY")
                                : moment(item.due).format("MMMM D, YYYY")}
                        </>
                      )}
                      {item.action === "agreement" && (
                        <>
                          {item?.sourceStatus === "Expired"
                            ? moment(item?.expiry_date).format("MMMM D, YYYY")
                            : item?.sourceStatus === "Completed"
                              ? moment(item.CompletedOn).format("MMMM D, YYYY")
                              : item?.sourceStatus === "Accepted"
                                ? moment(item.CompletedOn).format(
                                    "MMMM D, YYYY"
                                  )
                                : item?.sourceStatus === "Cancelled"
                                  ? moment(item.cancel_date).format("MMMM D, YYYY")
                                  : item?.sourceStatus === "Opened"
                                    ? moment(item.due).format("MMMM D, YYYY")
                                    : moment(item.due).format("MMMM D, YYYY")}
                        </>
                      )}
                      {item?.action === "invoice" && (
                        <>
                          {item?.sourceStatus?.toLowerCase() === "paid"
                            ? moment(item.CompletedOn).format("MMMM D, YYYY")
                            : item?.sourceStatud?.toLowerCase() === "cancelled"
                              ? moment(item.due).format("MMMM D, YYYY")
                              : item?.sourceStatus?.toLowerCase() === "opened"
                                ? moment(item.due).format("MMMM D, YYYY")
                                : moment(item.due).format("MMMM D, YYYY")}
                        </>
                      )}
                      {item?.action === "quotes" && (
                        <>
                          {item?.sourceStatus?.toLowerCase() === "accepted"
                            ? moment(item.CompletedOn).format("MMMM D, YYYY")
                            : item?.sourceStatud?.toLowerCase() === "canceled"
                              ? moment(item.due).format("MMMM D, YYYY")
                              : item?.sourceStatus?.toLowerCase() === "expired"
                                ? moment(item?.expiry_date).format(
                                    "MMMM D, YYYY"
                                  )
                                : item?.sourceStatus?.toLowerCase() === "open"
                                  ? moment(item.due).format("MMMM D, YYYY")
                                  : moment(item.due).format("MMMM D, YYYY")}
                        </>
                      )}
                    </p>
                    <p
                      className="w-fit lg:hidden underline text-blue text-sm hover:!text-btnPrimaryHover cursor-pointer pointer-events-auto"
                      onClick={() => {
                        const id = getActionNavigationSlug(item, clusterId);

                        navigate(`/${item?.action}/${id}`, {
                          state: { action: item?.action, id: id },
                        });
                      }}
                      data-testid={tid(
                        `action-center-${item?.id ?? index}-open-link`
                      )}
                      aria-label={`Open action center item ${index + 1}`}
                    >
                      Open
                    </p>
                  </div>
                  {/* Kept in the flow: floating it over the row let it cover the
                      text on hover. The invisible twin below reserves the same
                      width on the rows that are not hovered so nothing shifts. */}
                  {mouseHoverId === index ? (
                    <Button
                      text="Open"
                      className="hidden !py-[10px] !px-4 lg:grid place-content-center text-base rounded-md text-white leading-6 font-medium"
                      data-testid={tid(
                        `action-center-${item?.id ?? index}-open-button`
                      )}
                      aria-label={`Open action center item ${index + 1}`}
                      onClick={() => {
                        const id = getActionNavigationSlug(item, clusterId);
                        navigate(`/${item?.action}/${id}`);
                      }}
                    />
                  ) : (
                    <div className="py-[11px] px-[18px] w-[75.3px] lg:block hidden bg-white text-white">
                      Open
                    </div>
                  )}
                </div>
                </div>
              ))}
          </div>
        ) : (
          <div className="mt-14 flex justify-center">
            <div>
              <ActionNoData className="mx-auto" />
              <p className="text-txtSecondary text-base text-center mt-4">
                No tasks pending. You're all <br /> caught up!
              </p>
            </div>
          </div>
        )}
      </div>
      <Drawer
        enableOverlay={true}
        lockBackgroundScroll={true}
        direction="right"
        open={isAddCard}
        size={isLessThan590 ? "100vw" : "590px"}
        onClose={() => {
          setIsAddCard(false);
          setEditCardData(null);
          setIsEditCard(false);
        }}
        overlayColor="#000"
      >
        <AddEditCardForm
          show={isAddCard}
          setShow={setIsAddCard}
          isEdit={isEditCard}
          setIsEdit={setIsEditCard}
          onCloseClick={() => {
            onOldCardDataGet();
            onGetDashboardData();
          }}
          data={editCardData}
          setData={setEditCardData}
        />
      </Drawer>
      <Drawer
        enableOverlay={true}
        lockBackgroundScroll={true}
        direction="right"
        open={isAddBank}
        size={isLessThan590 ? "100vw" : "590px"}
        onClose={() => {
          setIsAddBank(false);
          setEditBankData(null);
        }}
        overlayColor="#000"
      >
        <AddEditBankForm
          show={isAddBank}
          setShow={setIsAddBank}
          isEdit={isEditBank}
          setIsEdit={setIsEditBank}
          onCloseClick={() => onOldCardDataGet()}
          data={editBankData}
          setData={setEditBankData}
        />
      </Drawer>
    </>
  );
};

export default PaymentMethodSection;
